import { useState, useRef, useEffect } from 'react'
import { cn } from '@global/utils/tailwind'
import { Button } from '@taxonomy-translation/components'
import Filter from 'images/filter.svg'
import { BottomSheet } from '../bottom-sheet'
import { FILTERS } from './constants'
import { FilterOption, TranslationKeys, TranslationStatus } from '../../../../../types'
import { t } from '@lingui/core/macro'

interface FilterButtonProps {
  onDelete?: (option: FilterOption) => void
  onClick?: (option: FilterOption) => void
  label: string
  value: string
  field: TranslationKeys
  className?: string
  disabled: boolean
}

const STATUS_COLOR = {
  [TranslationStatus.Match]: {
    bg: 'bg-[rgba(124,205,161,0.3)]',
  },
  [TranslationStatus.NoMatch]: {
    bg: 'bg-[rgba(228,27,12,0.2)]',
  },
  [TranslationStatus.Reviewed]: {
    bg: 'bg-[rgba(62,143,247,0.2)]',
  },
  [TranslationStatus.Excluded]: {
    bg: 'bg-[rgb(202,216,222)]',
  },
} as const

const FilterButton = ({
  value,
  onDelete,
  onClick,
  label,
  field,
  className = '',
  disabled,
}: FilterButtonProps) => {
  const filter = { value, label, field }

  const handleClick = event => {
    event.stopPropagation()
    if (!disabled && onClick) {
      onClick(filter)
    }
  }

  const handleDelete = event => {
    event.stopPropagation()
    onDelete?.(filter)
  }

  return (
    <button
      onClick={handleClick}
      className={cn(
        'px-3 py-1 rounded-[10px] bg-gray-200 flex items-center justify-between',
        disabled && 'opacity-50 pointer-events-none',
        STATUS_COLOR[value]?.bg,
        className,
      )}
    >
      <span className="text-sm">{label}</span>
      {onDelete && (
        <div
          role="button"
          tabIndex={0}
          onClick={handleDelete}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              handleDelete(event)
            }
          }}
          className="text-gray-500 hover:text-gray-800 ml-1 text-sm"
        >
          &#x2715;
        </div>
      )}
    </button>
  )
}

// TODO: include filters to mock data
export const FilterSelectorButton: React.FC<{
  activeFilters: FilterOption[]
  selectedFiltersInForm: FilterOption[]
  handleClickFilter: (filter: FilterOption) => void
  removeFilter: (filter: FilterOption) => void
  handleReset: () => void
  handleCancel: () => void
  handleApply: () => void
  channels: string[]
}> = ({
  selectedFiltersInForm,
  handleClickFilter,
  removeFilter,
  handleReset,
  handleCancel,
  handleApply,
  channels,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleApplyFilter = () => {
    handleApply()
    toggleDropdown()
  }

  const handleResetFilter = () => {
    handleReset()
    toggleDropdown()
  }

  // TODO: Edit the rendering and structure of the filter form
  return (
    <div className="relative">
      <Button onClick={toggleDropdown} className="px-6 py-2 text-[#64748B]" variant="outline">
        <Filter />
        {t`Filter`}
      </Button>

      {isDropdownOpen && (
        <BottomSheet ref={dropdownRef} className="p-0">
          <div className="max-h-[300px] overflow-y-auto">
            <div className="p-4">
              <h2 className="text-lg">{t`Filter`}</h2>
              <div className="flex gap-2 mt-4 flex-wrap">
                {selectedFiltersInForm.map((filter, index) => (
                  <FilterButton
                    key={filter.value + String(index)}
                    disabled={false}
                    label={filter.label}
                    value={filter.value}
                    field={filter.field}
                    onDelete={removeFilter}
                  />
                ))}
              </div>
            </div>
            <div className="gap-10 border-b p-4">
              <h3>{t`Status`}</h3>
              <div className="flex gap-2 mt-4 flex-wrap">
                {Object.entries(FILTERS.STATUS).map(([, item]) => (
                  <FilterButton
                    key={item.value}
                    label={item.label}
                    value={item.value}
                    field="status"
                    onClick={handleClickFilter}
                    disabled={selectedFiltersInForm.some(f => f.value === item.value)}
                  />
                ))}
              </div>
            </div>
            <div className="gap-10 border-b p-4">
              <h3>{t`Match confidence`}</h3>
              <div className="flex gap-2 mt-4 w-full whitespace-nowrap">
                {Object.entries(FILTERS.MATCH_CONFIDENCE).map(([, item]) => (
                  <FilterButton
                    key={item.value}
                    label={item.label}
                    value={item.value}
                    field="confidence"
                    onClick={handleClickFilter}
                    disabled={selectedFiltersInForm.some(f => f.value === item.value)}
                  />
                ))}
              </div>
            </div>
            <div className="gap-10 border-b p-4">
              <h3>{t`Stream`}</h3>
              <div className="flex gap-2 mt-4 w-full pb-2 flex-wrap">
                {channels.map(item => (
                  <FilterButton
                    key={item}
                    label={item}
                    value={item}
                    field="channel"
                    onClick={handleClickFilter}
                    disabled={selectedFiltersInForm.some(f => f.value === item)}
                    className="flex-shrink-0"
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="flex justify-between border-t p-4">
            <Button variant="text" onClick={handleResetFilter}>
              {t`Reset`}
            </Button>
            <Button onClick={handleCancel} variant="outline">
              {t`Cancel`}
            </Button>
            <Button variant="primary" onClick={handleApplyFilter}>
              {t`Apply`}
            </Button>
          </div>
        </BottomSheet>
      )}
    </div>
  )
}
